/**
 * The cells a straight drag from one cell to another passes through, excluding the cell it starts
 * from and including the one it ends on. Empty when the two are the same.
 *
 * Gesture reducers paint the cell each pointer sample reports, so how much of a drag registers
 * depends on how densely the platform samples it. That density is not something a puzzle should
 * depend on: the DOM delivers every `pointermove` it took, while React Native coalesces move events
 * whenever the JS thread falls behind, so the same drag paints a solid run in a browser and a dotted
 * one in the app. Filling the gap makes a gesture mean the same thing on both.
 *
 * Bresenham, so the path is the cells a straight line actually crosses — one step at a time, and
 * diagonally where the line runs diagonally. It deliberately does not include the orthogonal cells a
 * diagonal step passes the corner of: a diagonal drag paints a diagonal run today, and this exists to
 * recover skipped cells, not to widen the brush.
 */
export function cellsBetween(from: readonly [number, number], to: readonly [number, number]): [number, number][] {
  const [fromRow, fromCol] = from;
  const [toRow, toCol] = to;

  const deltaRow = Math.abs(toRow - fromRow);
  const deltaCol = Math.abs(toCol - fromCol);
  const stepRow = fromRow < toRow ? 1 : -1;
  const stepCol = fromCol < toCol ? 1 : -1;

  const path: [number, number][] = [];
  let row = fromRow;
  let col = fromCol;
  let error = deltaCol - deltaRow;

  // Every iteration moves at least one axis, so this can only run as far as the longest side; the
  // bound is belt and braces against a NaN coordinate turning a drag into a hang.
  const limit = deltaRow + deltaCol;
  while ((row !== toRow || col !== toCol) && path.length < limit) {
    const doubled = 2 * error;
    if (doubled > -deltaRow) {
      error -= deltaRow;
      col += stepCol;
    }
    if (doubled < deltaCol) {
      error += deltaCol;
      row += stepRow;
    }
    path.push([row, col]);
  }
  return path;
}

/**
 * The same, but never stepping diagonally: every cell in the returned path is orthogonally adjacent
 * to the one before it, and to the cell the drag started from.
 *
 * For a game whose moves are the lines *between* cells rather than the cells themselves. Such a move
 * only means something between two neighbours sharing an edge, so a diagonal step is not a move it
 * can make — a drag sampled from one cell to another two away has to be replayed as the two steps a
 * finger must have taken, or the whole stretch is silently dropped.
 *
 * Which axis a step takes is whichever keeps the path nearest the straight line, so a mostly
 * horizontal drag steps sideways and jogs vertically rather than the other way about.
 */
export function orthogonalCellsBetween(
  from: readonly [number, number],
  to: readonly [number, number]
): [number, number][] {
  const [fromRow, fromCol] = from;
  const [toRow, toCol] = to;

  const deltaRow = Math.abs(toRow - fromRow);
  const deltaCol = Math.abs(toCol - fromCol);
  const stepRow = fromRow < toRow ? 1 : -1;
  const stepCol = fromCol < toCol ? 1 : -1;

  const path: [number, number][] = [];
  let row = fromRow;
  let col = fromCol;
  /*
    How far ahead the path has run along the columns relative to the rows. A sideways step puts it
    further ahead, a vertical one brings it back, and the sign says which axis is owed the next step.
  */
  let error = 0;

  // One step per iteration along one axis, so this runs exactly the taxicab distance; the bound is
  // belt and braces against a NaN coordinate turning a drag into a hang.
  const limit = deltaRow + deltaCol;
  while ((row !== toRow || col !== toCol) && path.length < limit) {
    if (col === toCol) {
      row += stepRow;
    } else if (row === toRow) {
      col += stepCol;
    } else if (error > 0) {
      row += stepRow;
      error -= deltaCol;
    } else {
      col += stepCol;
      error += deltaRow;
    }
    path.push([row, col]);
  }
  return path;
}
